import { derivePipelineStatus, type PipelineLoan } from '../types';
import { getStrictFundedDate } from '../utils';
import { addCalendarDays, getBusinessDateString, parseAsLocalDate, toLocalDateString } from '@/utils/dateHelpers';

export type PacingMetricKey = 'opened' | 'locked' | 'funded';
export type PacingDispositionKey = 'prospect' | 'active' | 'funded' | 'archive' | 'adverse' | 'other';
export type PacingSignal = 'normal' | 'watch' | 'concern';

export interface PacingMetric {
  units: number;
  volume: number;
  businessDays: number;
  unitsPerDay: number;
  volumePerDay: number;
  projectedUnits: number | null;
  projectedVolume: number | null;
  previousUnitsPerDayPct: number | null;
  previousVolumePerDayPct: number | null;
  baselineUnitsPerDayPct: number | null;
  baselineVolumePerDayPct: number | null;
}

export interface PacingDispositionBucket {
  units: number;
  volume: number;
  pct: number;
}

export interface PacingMonthRow {
  key: string;
  label: string;
  startDate: string;
  endDate: string;
  isCurrentMonth: boolean;
  isPartial: boolean;
  businessDaysElapsed: number;
  businessDaysInMonth: number;
  metrics: Record<PacingMetricKey, PacingMetric>;
  dispositions: Record<PacingDispositionKey, PacingDispositionBucket>;
}

export interface PacingReportModel {
  rows: PacingMonthRow[];
  latestRow: PacingMonthRow | null;
  rangeBusinessDays: number;
}

const METRIC_KEYS: PacingMetricKey[] = ['opened', 'locked', 'funded'];

export const DISPOSITION_LABELS: Record<PacingDispositionKey, string> = {
  prospect: 'Prospect',
  active: 'Active',
  funded: 'Funded/Purchased',
  archive: 'Archive',
  adverse: 'Adverse',
  other: 'Other'
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function zeroMetric(days = 0): PacingMetric {
  return {
    units: 0,
    volume: 0,
    businessDays: days,
    unitsPerDay: 0,
    volumePerDay: 0,
    projectedUnits: null,
    projectedVolume: null,
    previousUnitsPerDayPct: null,
    previousVolumePerDayPct: null,
    baselineUnitsPerDayPct: null,
    baselineVolumePerDayPct: null
  };
}

function emptyDispositionBuckets(): Record<PacingDispositionKey, PacingDispositionBucket> {
  return {
    prospect: { units: 0, volume: 0, pct: 0 },
    active: { units: 0, volume: 0, pct: 0 },
    funded: { units: 0, volume: 0, pct: 0 },
    archive: { units: 0, volume: 0, pct: 0 },
    adverse: { units: 0, volume: 0, pct: 0 },
    other: { units: 0, volume: 0, pct: 0 }
  };
}

function weekdayOf(y: number, m: number, d: number): number {
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

function nthWeekday(y: number, m: number, weekday: number, n: number): number {
  const first = weekdayOf(y, m, 1);
  return 1 + ((weekday - first + 7) % 7) + (n - 1) * 7;
}

function lastWeekday(y: number, m: number, weekday: number): number {
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return lastDay - ((weekdayOf(y, m, lastDay) - weekday + 7) % 7);
}

function observed(y: number, m: number, d: number): { y: number; m: number; d: number } {
  const w = weekdayOf(y, m, d);
  const shift = w === 6 ? -1 : w === 0 ? 1 : 0;
  if (!shift) {
    return { y, m, d };
  }
  const t = new Date(Date.UTC(y, m - 1, d + shift));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

const FIXED_HOLIDAYS: Array<[number, number]> = [[1, 1], [6, 19], [7, 4], [11, 11], [12, 25]];
const holidayCache = new Map<number, Set<string>>();

export function federalHolidaySet(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) {
    return cached;
  }

  const set = new Set<string>();
  for (const yy of [year - 1, year, year + 1]) {
    for (const [m, d] of FIXED_HOLIDAYS) {
      const o = observed(yy, m, d);
      if (o.y === year) {
        set.add(`${o.m}-${o.d}`);
      }
    }
  }
  set.add(`1-${nthWeekday(year, 1, 1, 3)}`);
  set.add(`2-${nthWeekday(year, 2, 1, 3)}`);
  set.add(`5-${lastWeekday(year, 5, 1)}`);
  set.add(`9-${nthWeekday(year, 9, 1, 1)}`);
  set.add(`10-${nthWeekday(year, 10, 1, 2)}`);
  set.add(`11-${nthWeekday(year, 11, 4, 4)}`);
  holidayCache.set(year, set);
  return set;
}

export function isBusinessDay(dateStr: string): boolean {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) {
    return false;
  }
  const w = weekdayOf(y, m, d);
  if (w === 0 || w === 6) {
    return false;
  }
  return !federalHolidaySet(y).has(`${m}-${d}`);
}

export function countBusinessDays(startDate: string, endDate: string): number {
  if (endDate < startDate) {
    return 0;
  }

  let count = 0;
  let cursor = startDate;
  for (let guard = 0; cursor <= endDate && guard < 4000; guard++) {
    if (isBusinessDay(cursor)) {
      count += 1;
    }
    cursor = addCalendarDays(cursor, 1);
  }
  return count;
}

function monthStart(monthKey: string): string {
  return `${monthKey}-01`;
}

function monthEnd(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

function addMonths(monthKey: string, delta: number): string {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  return `${MONTH_SHORT[m - 1]} ${y}`;
}

function monthKeysBetween(startDate: string, endDate: string): string[] {
  const keys: string[] = [];
  let cursor = startDate.slice(0, 7);
  const end = endDate.slice(0, 7);
  while (cursor <= end) {
    keys.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  return keys;
}

function clampDate(value: string, min: string, max: string): string {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

export function eventDateToBusinessDay(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const midnightUtcMatch = value.match(/^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.\d+)?(?:Z|[+-]00(?::00)?)$/);
  if (midnightUtcMatch) {
    return midnightUtcMatch[1];
  }
  const parsed = parseAsLocalDate(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return getBusinessDateString(parsed);
}

export function getEffectiveLockDate(loan: PipelineLoan): string | null {
  return loan.rate_lock_buy_side_lock_date
    || loan.rate_lock_sell_side_lock_date
    || loan.rate_lock_request_lock_date
    || null;
}

export function getPacingDisposition(loan: PipelineLoan): PacingDispositionKey {
  const folder = loan.loan_folder;
  if (folder === '(Archive)' || folder === 'Archive') {
    return 'archive';
  }
  if (folder === 'Adverse Loans') {
    return 'adverse';
  }
  const status = derivePipelineStatus(loan);
  if (folder === 'Completed Loans' || status === 'funded' || status === 'purchased' || loan.funded_date || loan.purchased_date) {
    return 'funded';
  }
  if (folder === 'Prospect') {
    return 'prospect';
  }
  if (folder === 'Active') {
    return 'active';
  }
  return 'other';
}

export function getLoanPacingDate(loan: PipelineLoan, metric: PacingMetricKey): string | null {
  if (metric === 'opened') {
    return eventDateToBusinessDay(loan.creation_date);
  }
  if (metric === 'locked') {
    return eventDateToBusinessDay(getEffectiveLockDate(loan));
  }
  return eventDateToBusinessDay(getStrictFundedDate(loan));
}

function amount(loan: PipelineLoan): number {
  return Number(loan.trans_details_loan_amt) || 0;
}

function changePct(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}

function metricForMonth(
  loans: PipelineLoan[],
  metric: PacingMetricKey,
  startDate: string,
  endDate: string,
  businessDays: number
): PacingMetric {
  let units = 0;
  let volume = 0;
  for (const loan of loans) {
    const day = getLoanPacingDate(loan, metric);
    if (!day || day < startDate || day > endDate) {
      continue;
    }
    units += 1;
    volume += amount(loan);
  }

  return {
    ...zeroMetric(businessDays),
    units,
    volume,
    unitsPerDay: businessDays > 0 ? units / businessDays : 0,
    volumePerDay: businessDays > 0 ? volume / businessDays : 0
  };
}

function dispositionForMonth(
  loans: PipelineLoan[],
  startDate: string,
  endDate: string
): Record<PacingDispositionKey, PacingDispositionBucket> {
  const buckets = emptyDispositionBuckets();
  let totalUnits = 0;
  for (const loan of loans) {
    const day = getLoanPacingDate(loan, 'opened');
    if (!day || day < startDate || day > endDate) {
      continue;
    }
    const key = getPacingDisposition(loan);
    buckets[key].units += 1;
    buckets[key].volume += amount(loan);
    totalUnits += 1;
  }

  for (const bucket of Object.values(buckets)) {
    bucket.pct = totalUnits > 0 ? (bucket.units / totalUnits) * 100 : 0;
  }
  return buckets;
}

function baselineMetric(
  loans: PipelineLoan[],
  metric: PacingMetricKey,
  monthKey: string
): PacingMetric {
  let unitsPerDay = 0;
  let volumePerDay = 0;
  let months = 0;

  for (let i = 1; i <= 3; i++) {
    const key = addMonths(monthKey, -i);
    const start = monthStart(key);
    const end = monthEnd(key);
    const days = countBusinessDays(start, end);
    const m = metricForMonth(loans, metric, start, end, days);
    unitsPerDay += m.unitsPerDay;
    volumePerDay += m.volumePerDay;
    months += 1;
  }

  return {
    ...zeroMetric(0),
    unitsPerDay: months > 0 ? unitsPerDay / months : 0,
    volumePerDay: months > 0 ? volumePerDay / months : 0
  };
}

function applyComparisons(
  metric: PacingMetric,
  loans: PipelineLoan[],
  metricKey: PacingMetricKey,
  monthKey: string,
  businessDaysInMonth: number,
  isCurrentMonth: boolean
): PacingMetric {
  const previousKey = addMonths(monthKey, -1);
  const previous = metricForMonth(
    loans,
    metricKey,
    monthStart(previousKey),
    monthEnd(previousKey),
    countBusinessDays(monthStart(previousKey), monthEnd(previousKey))
  );
  const baseline = baselineMetric(loans, metricKey, monthKey);
  return {
    ...metric,
    projectedUnits: isCurrentMonth ? metric.unitsPerDay * businessDaysInMonth : null,
    projectedVolume: isCurrentMonth ? metric.volumePerDay * businessDaysInMonth : null,
    previousUnitsPerDayPct: changePct(metric.unitsPerDay, previous.unitsPerDay),
    previousVolumePerDayPct: changePct(metric.volumePerDay, previous.volumePerDay),
    baselineUnitsPerDayPct: changePct(metric.unitsPerDay, baseline.unitsPerDay),
    baselineVolumePerDayPct: changePct(metric.volumePerDay, baseline.volumePerDay)
  };
}

export function pacingSignal(deltaPct: number | null): PacingSignal {
  if (deltaPct !== null && deltaPct <= -20) {
    return 'concern';
  }
  if (deltaPct !== null && deltaPct <= -10) {
    return 'watch';
  }
  return 'normal';
}

export function buildPacingReportModel(
  loans: PipelineLoan[],
  range: { from: Date; to: Date },
  now: Date = new Date()
): PacingReportModel {
  const selectedStart = toLocalDateString(range.from);
  const selectedEnd = toLocalDateString(range.to);
  const today = getBusinessDateString(now);
  const effectiveEnd = selectedEnd >= today ? today : selectedEnd;

  if (effectiveEnd < selectedStart) {
    return { rows: [], latestRow: null, rangeBusinessDays: 0 };
  }

  const rows = monthKeysBetween(selectedStart, effectiveEnd).map((key): PacingMonthRow => {
    const startDate = clampDate(monthStart(key), selectedStart, effectiveEnd);
    const endDate = clampDate(monthEnd(key), selectedStart, effectiveEnd);
    const isCurrentMonth = key === today.slice(0, 7);
    const businessDaysElapsed = countBusinessDays(startDate, endDate);
    const businessDaysInMonth = countBusinessDays(monthStart(key), monthEnd(key));
    const metrics = {} as Record<PacingMetricKey, PacingMetric>;

    for (const metricKey of METRIC_KEYS) {
      const metric = metricForMonth(loans, metricKey, startDate, endDate, businessDaysElapsed);
      metrics[metricKey] = applyComparisons(
        metric,
        loans,
        metricKey,
        key,
        businessDaysInMonth,
        isCurrentMonth
      );
    }

    return {
      key,
      label: monthLabel(key),
      startDate,
      endDate,
      isCurrentMonth,
      isPartial: startDate !== monthStart(key) || endDate !== monthEnd(key),
      businessDaysElapsed,
      businessDaysInMonth,
      metrics,
      dispositions: dispositionForMonth(loans, startDate, endDate)
    };
  });

  return {
    rows,
    latestRow: rows.length > 0 ? rows[rows.length - 1] : null,
    rangeBusinessDays: countBusinessDays(selectedStart, effectiveEnd)
  };
}
